import { extraerEtiquetasNumeracion } from './docxNumbering'

const TD_TH_STYLE = 'border:1px solid #999;padding:3px 6px;font-size:9pt;font-family:Arial,Helvetica,sans-serif;vertical-align:top'

// Mammoth representa cada nivel de una lista numerada de Word como un <ol>/<ul>
// HTML independiente. Cuando la lista se interrumpe con contenido normal (párrafos,
// imágenes, tablas) -algo muy común en las fichas técnicas-, cada <ol> nuevo reinicia
// su numeración en 1 y los niveles "saltados" se envuelven en <ul> sintéticos, así que
// el número real de Word (1., 2., 3., 4. ...) nunca llega a verse en el HTML resultante.
// Por eso el número se calcula aparte (ver docxNumbering.ts, que sí lee numbering.xml
// completo: numId, w:start y el w:lvlText compuesto de cada nivel, p. ej. "%1.%2." para
// que un sub-punto de la sección 7 salga como "7.1." y no como "1.") y se escribe como
// texto plano al inicio del párrafo, quitándole a mammoth la numeración para que ya no
// intente envolverlo en una lista.
//
// `etiquetas` trae, en orden de aparición, la numeración ya resuelta de cada párrafo
// numerado del documento. Si por algún motivo no hay etiqueta disponible para un párrafo
// (documento sin numbering.xml legible, desfase de conteo, etc.) se usa como respaldo el
// contador simple por nivel que había antes.
function numerarListasWord(mammoth: typeof import('mammoth'), etiquetas: string[]) {
  const contadores: Record<number, number> = {}
  let indiceEtiqueta = 0

  // `transforms` existe en tiempo de ejecución (lib/index.js lo exporta) pero no
  // está declarado en los tipos de mammoth, de ahí el `any`.
  const transforms = (mammoth as any).transforms

  return transforms.paragraph((paragraph: any) => {
    const numbering = paragraph.numbering
    if (!numbering) return paragraph

    const nivel = Number(numbering.level)
    Object.keys(contadores)
      .map(Number)
      .filter(n => n > nivel)
      .forEach(n => { delete contadores[n] })
    contadores[nivel] = (contadores[nivel] || 0) + 1

    const etiquetaResuelta = etiquetas[indiceEtiqueta]
    indiceEtiqueta += 1

    const marcador = !numbering.isOrdered
      ? '• '
      : etiquetaResuelta !== undefined
        ? `${etiquetaResuelta} `
        : `${contadores[nivel]}. `
    const primeraRun = paragraph.children.find((c: any) => c.type === 'run')

    const runMarcador = {
      type: 'run',
      children: [{ type: 'text', value: marcador }],
      styleId: null,
      styleName: null,
      isBold: primeraRun ? primeraRun.isBold : false,
      isUnderline: false,
      isItalic: primeraRun ? primeraRun.isItalic : false,
      isStrikethrough: false,
      isAllCaps: false,
      isSmallCaps: false,
      verticalAlignment: 'baseline',
      font: primeraRun ? primeraRun.font : null,
      fontSize: primeraRun ? primeraRun.fontSize : null,
      highlight: null,
    }

    return { ...paragraph, numbering: null, children: [runMarcador, ...paragraph.children] }
  })
}

export async function convertirWordANotaHtml(file: File): Promise<string> {
  const mammoth = await import('mammoth')
  const arrayBuffer = await file.arrayBuffer()
  const etiquetas = await extraerEtiquetasNumeracion(arrayBuffer)
  const result = await mammoth.convertToHtml(
    { arrayBuffer },
    { transformDocument: numerarListasWord(mammoth, etiquetas) }
  )

  // Los estilos del Word (fuente/tamaño originales) vienen como atributos style="..."
  // en cada tag; se quitan y se reemplazan por estilos inline propios (no un <style>,
  // porque DOMPurify vacía el contenido de <style> al sanitizar y esas reglas nunca aplicarían).
  const html = result.value
    .replace(/\s*style="[^"]*"/gi, '')
    .replace(/<table(?=[ >])/gi, '<table style="border-collapse:collapse;width:100%;margin:4px 0"')
    .replace(/<td(?=[ >])/gi, `<td style="${TD_TH_STYLE}"`)
    .replace(/<th(?=[ >])/gi, `<th style="${TD_TH_STYLE};font-weight:bold;background:#e8e8e8;text-align:center"`)
    .replace(/<p(?=[ >])/gi, '<p style="margin:2px 0;font-size:9pt;font-family:Arial,Helvetica,sans-serif;text-align:justify"')
    .replace(/<li(?=[ >])/gi, '<li style="font-size:9pt;font-family:Arial,Helvetica,sans-serif"')
    .replace(/<img(?=[ >])/gi, '<img style="display:block;width:5cm;height:5cm;object-fit:contain;margin:6px auto"')

  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:9pt;line-height:1.4;text-align:justify">${html}</div>`
}
